/**
 * GalleryScreen — admin gallery (Stage 3.3e).
 *
 * Layout:
 *   [Album sidebar]   [Upload form]
 *                     [Sortable image grid]
 *
 * - Albums list on the left; "+ New album" button creates one with a slug-from-title.
 * - Image grid on the right: drag-to-reorder via dnd-kit, delete per item, alt-text
 *   inline-edit on click.
 * - Upload posts multipart to `/api/gallery/upload/` (REST, not GraphQL —
 *   Strawberry 0.316 + strawberry-django 0.84 rejected the GraphQL multipart
 *   spec with "Unsupported content type"). Backend transcodes every upload
 *   to WebP (original + medium + thumbnail) — see gallery/models.py.
 */
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  FolderPlus,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Upload as UploadIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "urql";

import { getToken } from "../auth/token";
import { cn } from "../lib/cn";
import { confirmDestructive, showError, showSuccess } from "../lib/sweet";

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const ADMIN_ALBUMS_QUERY = /* GraphQL */ `
  query AdminGalleryAlbums {
    adminGalleryAlbums {
      id
      title
      slug
      isPublished
      itemCount
      coverThumbnailUrl
    }
  }
`;

const ADMIN_ALBUM_DETAIL_QUERY = /* GraphQL */ `
  query AdminGalleryAlbumDetail($id: ID!) {
    adminGalleryAlbum(id: $id) {
      id
      title
      slug
      isPublished
      items {
        id
        altText
        sortOrder
        thumbnailUrl
        mediumUrl
        width
        height
      }
    }
  }
`;

const CREATE_ALBUM = /* GraphQL */ `
  mutation CreateAlbum($input: CreateAlbumInput!) {
    createAlbum(input: $input) { id title slug }
  }
`;

const DELETE_ITEM = /* GraphQL */ `
  mutation DeleteGalleryItem($id: ID!) {
    deleteGalleryItem(itemId: $id)
  }
`;

const REORDER = /* GraphQL */ `
  mutation Reorder($albumId: ID!, $ids: [ID!]!) {
    reorderGalleryItems(albumId: $albumId, itemIds: $ids) {
      id
      sortOrder
    }
  }
`;

// Gallery upload uses a dedicated REST endpoint rather than GraphQL multipart
// — Strawberry 0.316 + strawberry-django 0.84 reject multipart bodies even
// with multipart_uploads_enabled=True. The REST endpoint accepts the same
// JWT auth header and runs the same WebP transcoding pipeline. See
// backend/gallery/views.py.
const API_ORIGIN = (() => {
  const env = import.meta.env.VITE_GRAPHQL_URL;
  if (env) return new URL(env).origin;
  return import.meta.env.PROD
    ? "https://api.kurdishrojava.org.uk"
    : ""; // dev: relative — Vite proxies
})();

const UPLOAD_URL = `${API_ORIGIN}/api/gallery/upload/`;

/**
 * Django returns media URLs as relative (e.g. "/media/...") because
 * MEDIA_URL is just "/media/". The admin lives on admin.* but media is
 * served from api.* — so we have to prepend the API origin or the
 * browser resolves the relative URL against the admin host and 404s.
 */
function absMediaUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_ORIGIN}${url}`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AlbumRow {
  id: string;
  title: string;
  slug: string;
  isPublished: boolean;
  itemCount: number;
  coverThumbnailUrl: string | null;
}

interface ItemRow {
  id: string;
  altText: string;
  sortOrder: number;
  thumbnailUrl: string | null;
  mediumUrl: string | null;
  width: number | null;
  height: number | null;
}

interface AlbumDetail {
  id: string;
  title: string;
  slug: string;
  isPublished: boolean;
  items: ItemRow[];
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function GalleryScreen() {
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null);

  const [{ data: albumsData, fetching: albumsFetching }, refetchAlbums] =
    useQuery<{ adminGalleryAlbums: AlbumRow[] }>({
      query: ADMIN_ALBUMS_QUERY,
      requestPolicy: "cache-and-network",
    });

  // Auto-select first album on initial load
  useEffect(() => {
    if (!selectedAlbumId && albumsData?.adminGalleryAlbums?.length) {
      setSelectedAlbumId(albumsData.adminGalleryAlbums[0].id);
    }
  }, [albumsData, selectedAlbumId]);

  const [, createAlbum] = useMutation(CREATE_ALBUM);

  async function handleCreateAlbum() {
    const title = window.prompt("Album title (e.g. 'Newroz 2026')");
    if (!title || !title.trim()) return;
    const result = await createAlbum({ input: { title: title.trim() } });
    if (result.error) {
      await showError({ title: "Couldn't create album", text: result.error.message });
      return;
    }
    refetchAlbums({ requestPolicy: "network-only" });
    const newId = (result.data as { createAlbum: { id: string } } | undefined)?.createAlbum?.id;
    if (newId) setSelectedAlbumId(newId);
    showSuccess({ title: "Album created" });
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
      {/* Sidebar — albums */}
      <aside className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-medium uppercase tracking-wide text-stone-500">
            Albums
          </h2>
          <button
            type="button"
            onClick={handleCreateAlbum}
            className={cn(
              "inline-flex items-center gap-1 rounded-md bg-flag-red px-2.5 py-1",
              "text-xs font-medium text-white hover:bg-red-700",
            )}
          >
            <FolderPlus className="size-3.5" />
            New
          </button>
        </div>

        {albumsFetching && !albumsData ? (
          <div className="rounded-md border border-stone-200 bg-white p-4 text-center text-sm text-stone-500">
            <Loader2 className="mx-auto size-4 animate-spin" />
          </div>
        ) : albumsData?.adminGalleryAlbums?.length ? (
          <ul className="space-y-1">
            {albumsData.adminGalleryAlbums.map((album) => (
              <li key={album.id}>
                <button
                  type="button"
                  onClick={() => setSelectedAlbumId(album.id)}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors",
                    selectedAlbumId === album.id
                      ? "bg-red-50 text-red-700"
                      : "text-stone-700 hover:bg-stone-100",
                  )}
                >
                  <span className="truncate font-medium">{album.title}</span>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-2 py-0.5 text-xs",
                      selectedAlbumId === album.id
                        ? "bg-red-100 text-red-700"
                        : "bg-stone-200 text-stone-600",
                    )}
                  >
                    {album.itemCount}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-md border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-sm text-stone-500">
            No albums yet. Click <strong>New</strong> to create one.
          </div>
        )}
      </aside>

      {/* Main — album detail */}
      <section className="min-w-0">
        {selectedAlbumId ? (
          <AlbumPanel
            albumId={selectedAlbumId}
            onItemsChanged={() => refetchAlbums({ requestPolicy: "network-only" })}
          />
        ) : (
          <div className="rounded-lg border border-dashed border-stone-300 bg-white p-12 text-center text-stone-500">
            Select or create an album to begin uploading images.
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Album panel — upload + grid
// ---------------------------------------------------------------------------

function AlbumPanel({
  albumId,
  onItemsChanged,
}: {
  albumId: string;
  onItemsChanged: () => void;
}) {
  const [{ data, fetching, error }, refetchDetail] = useQuery<{
    adminGalleryAlbum: AlbumDetail | null;
  }>({
    query: ADMIN_ALBUM_DETAIL_QUERY,
    variables: { id: albumId },
    requestPolicy: "cache-and-network",
  });

  const [, deleteItem] = useMutation(DELETE_ITEM);
  const [, reorderItems] = useMutation(REORDER);

  // Local copy of items so drag updates feel instant; we sync to server in onDragEnd.
  const [orderedItems, setOrderedItems] = useState<ItemRow[]>([]);
  useEffect(() => {
    if (data?.adminGalleryAlbum) setOrderedItems(data.adminGalleryAlbum.items);
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  async function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedItems.findIndex((i) => i.id === active.id);
    const newIndex = orderedItems.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(orderedItems, oldIndex, newIndex);
    setOrderedItems(next);
    const result = await reorderItems({
      albumId,
      ids: next.map((i) => i.id),
    });
    if (result.error) {
      // Roll back on failure
      setOrderedItems(orderedItems);
      await showError({ title: "Couldn't save order", text: result.error.message });
    }
  }

  async function handleDelete(item: ItemRow) {
    const ok = await confirmDestructive({
      title: "Delete this image?",
      text: `"${item.altText}"\n\nThis removes the file from the server. It cannot be undone.`,
      confirmText: "Yes, delete",
    });
    if (!ok) return;
    const result = await deleteItem({ id: item.id });
    if (result.error) {
      await showError({ title: "Couldn't delete", text: result.error.message });
      return;
    }
    refetchDetail({ requestPolicy: "network-only" });
    onItemsChanged();
    showSuccess({ title: "Deleted" });
  }

  if (fetching && !data) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-12 text-center text-stone-500">
        <Loader2 className="mx-auto size-5 animate-spin" />
      </div>
    );
  }
  if (error || !data?.adminGalleryAlbum) {
    return (
      <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
        {error?.message ?? "Album not found."}
      </div>
    );
  }

  const album = data.adminGalleryAlbum;

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-stone-900">{album.title}</h2>
          <p className="mt-1 text-xs text-stone-500">
            /{album.slug} · {orderedItems.length} image{orderedItems.length === 1 ? "" : "s"}
            {album.isPublished ? null : " · DRAFT (hidden from public)"}
          </p>
        </div>
      </header>

      {/* Upload form */}
      <UploadForm
        albumId={albumId}
        onUploaded={() => {
          refetchDetail({ requestPolicy: "network-only" });
          onItemsChanged();
        }}
      />

      {/* Sortable grid */}
      {orderedItems.length === 0 ? (
        <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50 p-12 text-center text-sm text-stone-500">
          No images yet. Upload one above.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={orderedItems.map((i) => i.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {orderedItems.map((item) => (
                <SortableTile key={item.id} item={item} onDelete={() => handleDelete(item)} />
              ))}
            </div>
          </SortableContext>
          <p className="text-xs text-stone-500">
            Drag tiles to reorder. The new order saves automatically.
          </p>
        </DndContext>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload form
// ---------------------------------------------------------------------------

function UploadForm({
  albumId,
  onUploaded,
}: {
  albumId: string;
  onUploaded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [altText, setAltText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      await showError({ title: "Pick a file first" });
      return;
    }
    if (!altText.trim()) {
      await showError({ title: "Alt text is required", text: "Screen readers rely on it." });
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("album_id", albumId);
      form.append("alt_text", altText.trim());
      form.append("file", file, file.name);

      const token = getToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `JWT ${token}`;
      // NOTE: do NOT set Content-Type — the browser sets it (with the
      // multipart boundary) automatically when the body is a FormData.

      const res = await fetch(UPLOAD_URL, { method: "POST", headers, body: form });

      if (!res.ok) {
        let message = `${res.status} ${res.statusText}`;
        try {
          const body = await res.json();
          if (body?.errors?.[0]?.message) message = body.errors[0].message;
        } catch {
          /* response wasn't JSON — fall back to the status text */
        }
        await showError({ title: "Upload failed", text: message });
        return;
      }

      // Reset form on success
      setFile(null);
      setAltText("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      onUploaded();
      showSuccess({ title: "Uploaded" });
    } catch (err) {
      await showError({
        title: "Upload failed",
        text: err instanceof Error ? err.message : "Network error",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-stone-200 bg-white p-4"
    >
      <h3 className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-stone-500">
        <UploadIcon className="size-3.5" />
        Upload image
      </h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[auto_minmax(0,1fr)_auto]">
        <label
          className={cn(
            "inline-flex cursor-pointer items-center gap-2 rounded-md border border-stone-300 bg-white px-3 py-2 text-sm",
            "hover:bg-stone-50",
          )}
        >
          <ImageIcon className="size-4 text-stone-500" />
          <span className="truncate">{file ? file.name : "Choose file"}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <input
          type="text"
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
          placeholder="Alt text (required) — describe what's in the image"
          className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
        />
        <button
          type="submit"
          disabled={submitting || !file || !altText.trim()}
          className={cn(
            "inline-flex items-center justify-center gap-2 rounded-md bg-flag-red px-4 py-2 text-sm font-medium text-white",
            "hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60",
          )}
        >
          {submitting && <Loader2 className="size-4 animate-spin" />}
          {submitting ? "Uploading…" : "Upload"}
        </button>
      </div>
      <p className="mt-2 text-xs text-stone-500">
        JPEG / PNG / WebP / GIF, up to 15 MB. All uploads are converted to WebP for fast delivery.
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Sortable grid tile
// ---------------------------------------------------------------------------

function SortableTile({
  item,
  onDelete,
}: {
  item: ItemRow;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative overflow-hidden rounded-md border bg-white shadow-sm",
        isDragging ? "border-flag-red ring-2 ring-flag-red/30" : "border-stone-200",
      )}
    >
      {/* The image itself doubles as the drag handle. Wrap in a button so
          keyboard users can also reorder. */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="block aspect-square w-full cursor-grab touch-none active:cursor-grabbing"
        aria-label={`Reorder ${item.altText}`}
      >
        {item.thumbnailUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={absMediaUrl(item.thumbnailUrl)}
            alt={item.altText}
            className="size-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-stone-100 text-stone-400">
            <ImageIcon className="size-8" />
          </div>
        )}
      </button>

      {/* Hover overlay with delete button */}
      <button
        type="button"
        onClick={onDelete}
        className={cn(
          "absolute right-2 top-2 inline-flex size-7 items-center justify-center rounded-full",
          "bg-stone-900/70 text-white opacity-0 transition-opacity hover:bg-red-700",
          "group-hover:opacity-100 focus:opacity-100",
        )}
        aria-label={`Delete ${item.altText}`}
      >
        <Trash2 className="size-3.5" />
      </button>

      {/* Alt text caption strip */}
      <div className="border-t border-stone-200 px-2 py-1.5">
        <p className="truncate text-xs text-stone-600" title={item.altText}>
          {item.altText}
        </p>
      </div>
    </div>
  );
}
